import { useState, useEffect, useCallback } from 'react';
import * as api from '../services/api';
import { 
  isAuthenticatedForFavorites, 
  loadFavorites, 
  addToFavorites, 
  removeFromFavorites,
  checkFavoriteStatus 
} from '../utils/favoritesManager';

export const useFavoriteAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingFavoriteAction, setPendingFavoriteAction] = useState(null);
  const [pendingReviewAction, setPendingReviewAction] = useState(null);

  // Check authentication status on mount and when token changes
  useEffect(() => {
    const checkAuth = () => {
      const isAuth = isAuthenticatedForFavorites();
      setIsAuthenticated(isAuth);
      console.log('Authentication status:', isAuth);
    };

    checkAuth();
    
    // Listen for storage changes (when token is added/removed)
    const handleStorageChange = (e) => {
      if (e.key === 'access_token') {
        checkAuth();
      }
    };

    // Also listen for custom events when token is removed programmatically
    const handleTokenRemoved = () => {
      setIsAuthenticated(false);
      console.log('Token removed, setting authenticated to false');
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('tokenRemoved', handleTokenRemoved);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('tokenRemoved', handleTokenRemoved);
    };
  }, []);

  // Execute the actual favorite action
  const executeFavoriteAction = useCallback(async (action, objectId, objectType) => {
    setLoading(true);
    try {
      console.log(`Executing favorite action: ${action} for ${objectType} ${objectId}`);
      
      if (action === 'add') {
        const result = await api.addToFavorites({
          favorited_object_id: objectId,
          favorited_object_type: objectType
        });
        console.log('Add to favorites result:', result);
      } else if (action === 'remove') {
        const favorites = await api.getUserFavorites();
        console.log('Current favorites:', favorites);
        
        const favoriteToRemove = favorites.find(
          fav => fav.favorited_object_type === objectType && fav.favorited_object_id === objectId
        );
        
        console.log('Favorite to remove:', favoriteToRemove);
        
        if (favoriteToRemove) {
          const result = await api.removeFromFavorites(favoriteToRemove.id);
          console.log('Remove from favorites result:', result);
        } else {
          console.warn('Favorite not found to remove');
        }
      }
      
      console.log('Favorite action completed successfully');
      return true; // Success
    } catch (error) {
      console.error('Error executing favorite action:', error);
      console.error('Error details:', {
        action,
        objectId,
        objectType,
        errorMessage: error.message,
        errorStack: error.stack
      });
      
      // If authentication error, update auth status
      if (error.message === 'Token expiré ou invalide' || error.status === 401) {
        console.log('Authentication error detected, updating auth status');
        setIsAuthenticated(false);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        
        // Déclencher un événement personnalisé pour notifier les autres composants
        window.dispatchEvent(new CustomEvent('tokenRemoved'));
      }
      
      return false; // Error
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle favorite action with authentication check
  const handleFavoriteAction = useCallback(async (action, objectId, objectType) => {
    console.log('handleFavoriteAction called:', { action, objectId, objectType, isAuthenticated });
    
    if (!isAuthenticated) {
      console.log('User not authenticated, showing login popup');
      // Store the pending action and show login popup
      setPendingFavoriteAction({ action, objectId, objectType });
      setShowLoginPopup(true);
      return false; // Return false to indicate action not completed yet
    }

    // User is authenticated, proceed with action
    console.log('User authenticated, executing action');
    return await executeFavoriteAction(action, objectId, objectType);
  }, [isAuthenticated, executeFavoriteAction]);

  // Handle successful login
  const handleLoginSuccess = useCallback(async (userData) => {
    console.log('Login successful, user data:', userData);
    setIsAuthenticated(true);
    
    // Execute pending favorite action if any
    if (pendingFavoriteAction) {
      console.log('Executing pending favorite action:', pendingFavoriteAction);
      const { action, objectId, objectType } = pendingFavoriteAction;
      const success = await executeFavoriteAction(action, objectId, objectType);
      console.log('Pending action execution result:', success);
      setPendingFavoriteAction(null);
    }
    
    // Execute pending review action if any
    if (pendingReviewAction) {
      console.log('Executing pending review action:', pendingReviewAction);
      setPendingReviewAction(null);
      // Reprendre l'action demandée avant connexion (avis, demande traiteur...)
      pendingReviewAction.callback?.();
    }
  }, [pendingFavoriteAction, pendingReviewAction, executeFavoriteAction]);

  // Close login popup
  const closeLoginPopup = useCallback(() => {
    console.log('Closing login popup');
    setShowLoginPopup(false);
    setPendingFavoriteAction(null);
    setPendingReviewAction(null);
  }, []);

  // Check if an item is in favorites
  const checkFavoriteStatus = useCallback(async (objectId, objectType) => {
    if (!isAuthenticated) {
      console.log('Not authenticated, returning false for favorite status');
      return false;
    }
    
    try {
      console.log(`Checking favorite status for ${objectType} ${objectId}`);
      const status = await api.checkFavoriteStatus(objectId, objectType);
      console.log('Favorite status result:', status);
      return status;
    } catch (error) {
      console.error('Error checking favorite status:', error);
      return false;
    }
  }, [isAuthenticated]);

  // Handle review action with authentication check
  const handleReviewAction = useCallback(async (action, callback) => {
    console.log('handleReviewAction called:', { action, isAuthenticated });
    
    if (!isAuthenticated) {
      console.log('User not authenticated, showing login popup for review action');
      setPendingReviewAction({ type: action, callback });
      setShowLoginPopup(true);
      return false;
    }

    // User is authenticated, proceed with action
    console.log('User authenticated, executing review action');
    if (callback) {
      callback();
    }
    return true;
  }, [isAuthenticated]);

  return {
    isAuthenticated,
    showLoginPopup,
    loading,
    handleFavoriteAction,
    handleReviewAction,
    handleLoginSuccess,
    closeLoginPopup,
    checkFavoriteStatus,
    executeFavoriteAction
  };
};
